import { useCallback, useContext, useState } from 'react';
import moment from 'moment';
import {
  ADJUSTMENT_25TH_PERCENTILE,
  ADJUSTMENT_75TH_PERCENTILE,
  ADJUSTMENT_MEAN,
  ADJUSTMENT_MEDIAN,
  ADJUSTMENT_SELECTION_A,
  ADJUSTMENT_SELECTION_B,
  SPECIFIED,
} from 'common/constants/valuations';
import { PERCENTILE } from 'common/formulas/math.js/PERCENTILE';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { Format } from 'common/types/format';
import { ValuationsApproach } from 'common/types/valuation';
import { compKeyMap } from 'pages/Valuations/approaches/backsolveApproach/MarketAdjustmentLedger/util/constants';
import { useRefreshHelpersWithContext } from 'pages/Valuations/components/RefreshGPCOption/hooks';
import { BacksolveAttributes } from 'pages/ValuationsAllocation/types';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { useGetCompsByDate } from 'services/hooks/valuation';
import { ISO_DATE_FORMAT, Logger, toString } from 'utilities';
import getExchangeRate from 'utilities/getExchangeRate';
import { DEFAULT_MARKET_ADJUSTMENT_DECIMAL_PLACES } from '../constants';
import { ValuationApproachBackSolveExtended } from '../types';

interface UseMarketAdjustmentTableChangeProps {
  gpcApproach: ValuationsApproach | undefined;
  approach: { valuations_approach_backsolve: ValuationApproachBackSolveExtended };
  setMarketAdjustment: (value: string) => void;
  backsolveAttributes?: BacksolveAttributes;
}

/**
 * Retrieves the exchange rate between two currencies for a given date.
 * Returns 1 if currencies are the same or if exchange rate cannot be fetched.
 */
const getExchangeRateForMarketAdjustment = async (
  sourceCurrency: string,
  targetCurrency: string,
  measurementDateId: string | number | undefined,
  currencyDate: string
): Promise<number> => {
  // No conversion needed if currencies are the same
  if (!sourceCurrency || !targetCurrency || sourceCurrency === targetCurrency || !measurementDateId) {
    return 1;
  }

  try {
    const exchangeRateValue = await getExchangeRate(
      sourceCurrency,
      targetCurrency,
      measurementDateId.toString(),
      currencyDate
    );
    return Number(exchangeRateValue);
  } catch (error) {
    Logger.error('Error fetching exchange rate for market adjustment', error);
    // Return 1 if exchange rate fetch fails to continue with unconverted value
    return 1;
  }
};

export const useMarketAdjustmentTableChange = ({
  gpcApproach,
  approach,
  setMarketAdjustment,
  backsolveAttributes,
}: UseMarketAdjustmentTableChangeProps) => {
  const [fetchGpcCompData] = useGetCompsByDate();
  const { getFlattenedComparisons } = useRefreshHelpersWithContext();
  const [{ firmId }] = useStore();
  const [format] = useFormat({ page: 'CAP_TABLE_CURRENCY_PAGE' }) as [Format, any];
  const [isLoadingMarketAdjustment, setIsLoadingMarketAdjustment] = useState(false);
  const { measurementDate, marketCurrency } = useContext(ValuationContext);

  // Helper function to get metric value from company data
  const getMetricValue = useCallback((comp: any, metric: string) => {
    const metricKey = Object.keys(compKeyMap).find(key => compKeyMap[key as keyof typeof compKeyMap] === metric);
    return metricKey ? Number(comp[metricKey] || 0) : 0;
  }, []);

  const onMarketAdjustmentTableChange = useCallback(
    async (value: string | number) => {
      if (value && gpcApproach) {
        const marketAdjustmentDecimalPlaces = Number(backsolveAttributes
          ?.backsolveApproachAttrs
          ?.market_adjustment
          ?.decimal_places ?? DEFAULT_MARKET_ADJUSTMENT_DECIMAL_PLACES
        );
        setIsLoadingMarketAdjustment(true);
        try {
          const asOfDateMoment = moment(value as string).format(ISO_DATE_FORMAT);
          const identifiers = (gpcApproach?.valuations_approach_gpc?.gpc_comparison ?? [])
            .map(comp => comp.cap_iq_id)
            .filter((ticker): ticker is string => Boolean(ticker));

          if (identifiers.length > 0 && format.currency.code) {
            const asOfCompsData = await fetchGpcCompData(
              identifiers,
              asOfDateMoment,
              format.currency.code as string,
              firmId,
              true
            );
            if (asOfCompsData?.results) {
              const flattenedComps = getFlattenedComparisons(asOfCompsData.results as any);
              // Update existing public company status entries with new data and calculate adjustments
              const existingStatuses = approach?.valuations_approach_backsolve?.public_companies_status || [];
              const gpcComparison = gpcApproach?.valuations_approach_gpc?.gpc_comparison || [];
              const metric = approach?.valuations_approach_backsolve?.metric;
              
              // Get exchange rate once if currencies differ (same for all comps)
              const asOfCurrency = format.currency.code as string;
              const exchangeRate = await getExchangeRateForMarketAdjustment(
                asOfCurrency,
                marketCurrency,
                measurementDate?.id,
                asOfDateMoment
              );
              
              const updatedStatuses = existingStatuses.map((existingStatus: any) => {
                const matchingAsOfComp = flattenedComps.find((comp: any) => comp.cap_iq_id === existingStatus.cap_iq_id);
                const matchingMdComp = gpcComparison.find((comp: any) => comp.cap_iq_id === existingStatus.cap_iq_id);
                
                if (matchingAsOfComp && matchingMdComp && metric) {
                  const {
                    ltm_revenue: asOfLtmRevenue,
                    ltm_ebitda: asOfLtmEbitda,
                    ntm_revenue: asOfNtmRevenue,
                    ntm_ebitda: asOfNtmEbitda,
                    market_cap: asOfMarketCap,
                    enterprise_value: asOfEnterpriseValue,
                    ticker_symbol,
                  } = matchingAsOfComp;
                  
                  // Get the metric values for calculation
                  // Column B: As-of date data (in cap table currency)
                  const asOfMetricValue = getMetricValue(matchingAsOfComp, metric) * exchangeRate;
                  // Column C: Market date data (from GPC comparisons, in market currency)
                  const mdMetricValue = getMetricValue(matchingMdComp, metric);
                  
                  // Calculate the percentage change: (C - B) / B
                  const percentageChange = asOfMetricValue !== 0 ? (mdMetricValue - asOfMetricValue) / asOfMetricValue : 0;
                  
                  return {
                    ...existingStatus,
                    ltm_revenue: toString((Number(asOfLtmRevenue ?? 0)) * exchangeRate),
                    ltm_ebitda: toString((Number(asOfLtmEbitda ?? 0)) * exchangeRate),
                    ntm_revenue: toString((Number(asOfNtmRevenue ?? 0)) * exchangeRate),
                    ntm_ebitda: toString((Number(asOfNtmEbitda ?? 0)) * exchangeRate),
                    market_cap: toString((Number(asOfMarketCap ?? 0)) * exchangeRate),
                    enterprise_value: toString((Number(asOfEnterpriseValue ?? 0)) * exchangeRate),
                    ticker_symbol: ticker_symbol ?? existingStatus.ticker_symbol,
                    // Store the calculated percentage change for the selected metric
                    percent_change: percentageChange.toFixed(marketAdjustmentDecimalPlaces),
                  };
                }
                
                return existingStatus;
              });

              // Calculate overall percentage change for all companies using the selected adjustment method
              const validPercentageChanges = updatedStatuses
                .filter((status: any) => (status as any).percent_change && status.enabled)
                .map((status: any) => (status as any).percent_change);
              
              const adjustmentSelected = approach?.valuations_approach_backsolve?.adjustment_selected;
              const percentileSelectionA = approach?.valuations_approach_backsolve?.percentile_selection_a;
              const percentileSelectionB = approach?.valuations_approach_backsolve?.percentile_selection_b;
              let overallPercentageChange = 0;
              
              if (validPercentageChanges.length > 0) {
                // Convert to string array for PERCENTILE function
                const percentageChangesAsStrings = validPercentageChanges.map((change: number) => change.toString());
                
                switch (adjustmentSelected) {
                  case ADJUSTMENT_MEAN:
                    overallPercentageChange = validPercentageChanges.reduce((sum: number, change: number) => sum + change, 0) / validPercentageChanges.length;
                    break;
                  case ADJUSTMENT_MEDIAN:
                    overallPercentageChange = PERCENTILE(percentageChangesAsStrings, 50);
                    break;
                  case ADJUSTMENT_SELECTION_A:
                    if (percentileSelectionA) {
                      overallPercentageChange = PERCENTILE(percentageChangesAsStrings, percentileSelectionA);
                    }
                    break;
                  case ADJUSTMENT_SELECTION_B:
                    if (percentileSelectionB) {
                      overallPercentageChange = PERCENTILE(percentageChangesAsStrings, percentileSelectionB);
                    }
                    break;
                  case ADJUSTMENT_25TH_PERCENTILE:
                    overallPercentageChange = PERCENTILE(percentageChangesAsStrings, 25);
                    break;
                  case ADJUSTMENT_75TH_PERCENTILE:
                    overallPercentageChange = PERCENTILE(percentageChangesAsStrings, 75);
                    break;
                  case SPECIFIED:
                    overallPercentageChange = Number(approach?.valuations_approach_backsolve?.market_adjustment ?? 0);
                    break;
                  default:
                    break;
                }
              }
              const newMarketAdjustment = Number(overallPercentageChange.toFixed(marketAdjustmentDecimalPlaces));
              // Update the approach with the updated public companies status
              // eslint-disable-next-line no-param-reassign
              approach.valuations_approach_backsolve.public_companies_status = updatedStatuses;
              // Update the marketAdjustment state to prevent reversion
              setMarketAdjustment(toString(newMarketAdjustment));
            }
          }
        } catch (error) {
          // Error handling for public company data fetching
          Logger.error('Error fetching public company data for market adjustment', error);
        } finally {
          setIsLoadingMarketAdjustment(false);
        }
      }
    },
    [gpcApproach, backsolveAttributes, format, fetchGpcCompData, firmId, getFlattenedComparisons, approach, getMetricValue, setMarketAdjustment, measurementDate, marketCurrency]
  );

  return { onMarketAdjustmentTableChange, isLoadingMarketAdjustment };
};
